import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { User } from '../types';
import api from '../utils/api';
import StaffRegistrationForm from '../components/StaffRegistrationForm';
import StaffDetailsModal from '../components/StaffDetailsModal';
import CustomSelect from '../components/CustomSelect';
import RowActions from '../components/RowActions';

interface StaffPageData {
  staff: User[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

const Staff: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const [staffData, setStaffData] = useState<StaffPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [roleFilter, setRoleFilter] = useState('all');
  const [staffTypeFilter, setStaffTypeFilter] = useState('all');
  const [viewingStaff, setViewingStaff] = useState<User | null>(null);

  useEffect(() => {
    fetchStaff();
  }, [currentPage, searchTerm, roleFilter, staffTypeFilter]); // fetchStaff is defined within component, so it's safe to exclude

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...(searchTerm && { search: searchTerm }),
        ...(roleFilter !== 'all' && { role: roleFilter }),
        ...(staffTypeFilter !== 'all' && { staffType: staffTypeFilter })
      });

      const response = await api.get(`/users/staff?${params}`);
      
      if (response.data.success) {
        setStaffData(response.data.data);
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to fetch staff members');
    } finally {
      setLoading(false);
    }
  };

  const handleStaffCreated = (newStaff: User) => {
    fetchStaff(); // Refresh the list
    setShowCreateModal(false);
  };

  const handleEditStaff = (staff: User) => {
    setEditingStaff(staff);
  };

  const handleUpdateStaff = async (staffId: number, updateData: Partial<User>) => {
    try {
      const response = await api.put(`/users/staff/${staffId}`, updateData);
      
      if (response.data.success) {
        fetchStaff(); // Refresh the list
        setEditingStaff(null);
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update staff member');
    }
  };

  const handleDeactivateStaff = async (staffId: number) => {
    if (!window.confirm('Are you sure you want to deactivate this staff member?')) {
      return;
    }

    try {
      const response = await api.delete(`/users/staff/${staffId}`);
      
      if (response.data.success) {
        fetchStaff(); // Refresh the list
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to deactivate staff member');
    }
  };

  const handleResetPassword = async (staffId: number) => {
    const newPassword = window.prompt('Enter new password (minimum 6 characters):');
    
    if (!newPassword) return;
    
    if (newPassword.length < 6) {
      window.alert('Password must be at least 6 characters long');
      return;
    }

    try {
      const response = await api.put(`/users/staff/${staffId}/password`, {
        password: newPassword
      });
      
      if (response.data.success) {
        window.alert('Password updated successfully');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update password');
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-primary-100 text-primary-800';
      case 'staff':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStaffTypeBadgeColor = (staffType: string) => {
    switch (staffType) {
      case 'bank':
        return 'bg-orange-100 text-orange-800';
      case 'agency':
        return 'bg-yellow-100 text-yellow-800';
      case 'regular':
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  const getStaffTypeLabel = (staffType: string) => {
    switch (staffType) {
      case 'bank':
        return 'Bank';
      case 'agency':
        return 'Agency';
      case 'regular':
      default:
        return 'Regular';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <div className="text-red-500">
          <svg className="mx-auto h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.502 0L4.232 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-red-800">Access Denied</h3>
          <p className="mt-1 text-sm text-red-600">You need administrator privileges to access staff management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Staff Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage staff members, roles, and permissions
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            onClick={() => setShowCreateModal(true)}
            className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Staff Member
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white card-list p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Role
            </label>
            <CustomSelect
              value={roleFilter}
              onChange={setRoleFilter}
              options={[
                { value: 'all', label: 'All Roles' },
                { value: 'admin', label: 'Admin' },
                { value: 'staff', label: 'Staff' },
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Staff Type
            </label>
            <CustomSelect
              value={staffTypeFilter}
              onChange={setStaffTypeFilter}
              options={[
                { value: 'all', label: 'All Types' },
                { value: 'regular', label: 'Regular' },
                { value: 'bank', label: 'Bank' },
                { value: 'agency', label: 'Agency' },
              ]}
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setRoleFilter('all');
                setStaffTypeFilter('all');
                setCurrentPage(1);
              }}
              className="px-4 py-2 text-base font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Staff List */}
      <div className="bg-white card-list overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-sm text-gray-500">Loading staff members...</p>
          </div>
        ) : staffData && staffData.staff.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {staffData.staff.map((staffMember) => (
              <li key={staffMember.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <img
                        src={staffMember.photo || '/images/profiles/placeholder-staff.jpg'}
                        alt={staffMember.name}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center flex-wrap gap-1">
                        <div className="text-sm font-medium text-gray-900">
                          {staffMember.name}
                        </div>
                        <span className={`inline-flex items-center px-2.5 py-0.5 badge-pill ${getRoleBadgeColor(staffMember.role)}`}>
                          {staffMember.role}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 badge-pill ${getStaffTypeBadgeColor(staffMember.staffType || 'regular')}`}>
                          {getStaffTypeLabel(staffMember.staffType || 'regular')}
                        </span>
                        {!staffMember.isActive && (
                          <span className="inline-flex items-center px-2.5 py-0.5 badge-pill bg-red-100 text-red-800">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {staffMember.email}
                      </div>
                      <div className="text-xs text-gray-400">
                        Joined {formatDate(staffMember.createdAt)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <RowActions
                      actions={[
                        { label: 'View Details', tone: 'primary', onClick: () => setViewingStaff(staffMember) },
                        { label: 'Edit', tone: 'warning', onClick: () => handleEditStaff(staffMember) },
                        { label: 'Reset Password', tone: 'success', onClick: () => handleResetPassword(staffMember.id) },
                        { label: 'Deactivate', tone: 'danger', hidden: staffMember.id === user?.id, onClick: () => handleDeactivateStaff(staffMember.id) },
                      ]}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No staff members found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || roleFilter !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by adding the first staff member.'
              }
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {staffData && staffData.pagination.totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={!staffData.pagination.hasPrev}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => prev + 1)}
              disabled={!staffData.pagination.hasNext}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">
                  {((currentPage - 1) * 10) + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * 10, staffData.pagination.totalCount)}
                </span>{' '}
                of{' '}
                <span className="font-medium">{staffData.pagination.totalCount}</span>{' '}
                results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={!staffData.pagination.hasPrev}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={!staffData.pagination.hasNext}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Create Staff Modal */}
      <StaffRegistrationForm
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleStaffCreated}
      />

      {/* View Staff Details Modal */}
      {viewingStaff && (
        <StaffDetailsModal
          staffId={viewingStaff.id}
          isOpen={!!viewingStaff}
          onClose={() => setViewingStaff(null)}
        />
      )}

      {/* Edit Staff Modal */}
      {editingStaff && (
        <EditStaffModal
          staff={editingStaff}
          onClose={() => setEditingStaff(null)}
          onUpdate={handleUpdateStaff}
        />
      )}
    </div>
  );
};

// Edit Staff Modal Component
interface EditStaffModalProps {
  staff: User;
  onClose: () => void;
  onUpdate: (staffId: number, updateData: Partial<User>) => void;
}

const EditStaffModal: React.FC<EditStaffModalProps> = ({ staff, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    name: staff.name,
    email: staff.email,
    role: staff.role,
    staffType: staff.staffType || 'regular',
    isActive: staff.isActive
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await onUpdate(staff.id, formData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Edit Staff Member</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <CustomSelect
                value={formData.role}
                onChange={(v) => setFormData(prev => ({ ...prev, role: v as 'admin' | 'staff' }))}
                options={[
                  { value: 'staff', label: 'Staff' },
                  { value: 'admin', label: 'Admin' },
                ]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Staff Type
              </label>
              <CustomSelect
                value={formData.staffType}
                onChange={(v) => setFormData(prev => ({ ...prev, staffType: v as 'regular' | 'bank' | 'agency' }))}
                options={[
                  { value: 'regular', label: 'Regular' },
                  { value: 'bank', label: 'Bank' },
                  { value: 'agency', label: 'Agency' },
                ]}
              />
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700">Active</span>
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-base font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-base font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : 'Update Staff'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Staff;